import PropTypes from 'prop-types'
import { Edit, TrashAlt } from '@styled-icons/boxicons-solid'
import { Restore } from '@styled-icons/material'
import { formatEmptyValue, humanizeBool } from '../../../utils/common'
import LinkWithStatus from '../../common/LinkWithStatus'
import { deleteRequest, getRequest } from '../../../services/base'
import { useContext, useEffect, useState } from 'react'
import CreateUpdateUser from './create_update_user'
import { NavContext } from '../../../context/global_context'
import CheckBox from '../../common/CheckBox'
import Pagination from '../../common/Pagination'
import NoRecordsFound from '../../common/NoRecordsFound'

const UserListRow = (props) => {
  return (
    <div className="flex flex-row flex-wrap items-center bg-white w-full py-2 px-3 border-b border-tracer-dark-blue text-black">
      <div className="flex flex-row w-full">
        <div className="flex w-4/12 pl-10 items-center">{props.email}</div>
        <div className="flex w-3/12 pl-6 items-center table_text">
          {formatEmptyValue(props.first_name)}
        </div>
        <div className="flex w-3/12 pl-6 items-center table_text">
          {formatEmptyValue(props.last_name)}
        </div>
        <div className="w-2/12 pl-6">{humanizeBool(props.is_admin)}</div>
        <div className="w-2/12 pl-6">{humanizeBool(props.is_active)}</div>

        <div className="flex w-2/12 pl-6 justify-end">
          <div>
            <span
              onClick={() => {
                props.onEdit()
              }}
              className="cursor-pointer p-2"
            >
              <Edit size="2rem" color="#0189E9" />
            </span>
            <span
              onClick={() => {
                props.onDelete()
              }}
              className="cursor-pointer p-2"
            >
              {props.showDeleted ? (
                <Restore size="2rem" color="#0189E9" />
              ) : (
                <TrashAlt size="2rem" color="#0189E9" />
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

UserListRow.propTypes = {
  email: PropTypes.string.isRequired,
  first_name: PropTypes.string.isRequired,
  last_name: PropTypes.string.isRequired,
  is_admin: PropTypes.bool,
  is_active: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showDeleted: PropTypes.bool,
}

const UserList = (props) => {
  const [state, dispatch] = useContext(NavContext)
  const [userListData, setUserListData] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current_page: 1,
  })
  const [showModal, setShowModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState({})
  const [showDeleted, setShowDeleted] = useState(false)

  useEffect(() => {
    getUserList(null, true)
  }, [])

  const getUserList = async (url = null, is_active = true) => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Fetching User`s Data',
        showLoader: true,
      },
      notification: {},
    })
    let response = await getRequest(
      url ? url : `/api/users/?search=${searchValue}&is_active=${is_active}`
    )
    setUserListData(response.results)
    setPagination(response.pagination)
    dispatch({
      type: 'loader',
      data: {},
      notification: {},
    })
  }

  const deleteUser = async (user_obj) => {
    if (
      window.confirm(
        `Are you sure you want to ${showDeleted ? 'Restore' : 'Delete'} ${user_obj.email}`
      )
    ) {
      let res = await deleteRequest(
        `/api/users/delete/${user_obj.email}/${!showDeleted}/`
      )
      getUserList(null, !showDeleted)

      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: `User Deleted Successfully`,
        },
      })
    }
  }

  return (
    <>
      <CreateUpdateUser
        showModal={showModal}
        setShowModal={setShowModal}
        getUserList={getUserList}
        selectedUser={selectedUser}
        showDeleted={showDeleted}
      />
      <form
        className="flex flex-col w-full space-y-2 pb-5"
        acceptCharset="UTF-8"
        onSubmit={(e) => {
          e.preventDefault()
          getUserList(null, !showDeleted)
        }}
      >
        <div className="flex space-x-5">
          <div className="w-2/3">
            <input
              autoComplete="off"
              className="w-full bg-white h-10 px-5 pr-5 rounded focus:outline-none text-black"
              placeholder="Enter to search"
              type="search"
              name="search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value)
              }}
            />
          </div>
          <button className="bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded">
            Search
          </button>
          <div className="w-1/3 flex items-center">
            <CheckBox
              checked={showDeleted}
              onChange={(e) => {
                setShowDeleted(e.target.checked)
                getUserList(null, !e.target.checked)
              }}
            />
            <span className="pl-2">
              {showDeleted ? 'Hide Deleted Users' : 'Show Deleted Users'}
            </span>
          </div>
          <div className="w-2/3 font-light text-right space-x-5">
            <LinkWithStatus
              href={`/administration/user/new`}
              className={
                'cursor-pointer inline-block bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded'
              }
              title="Create New User"
              onClickFunc={() => {
                setSelectedUser({})
                setShowModal(true)
              }}
            />
          </div>
        </div>
      </form>
      <div className="font-light">
        <div className="flex flex-row w-full bg-tracer-lt-blue rounded-t-lg text-white px-3 text-sm">
          <div className="flex w-4/12 pl-10 items-center">Email</div>
          <div className="flex w-3/12 pl-6 items-center">First Name</div>
          <div className="flex w-3/12 pl-6 items-center">Last Name</div>
          <div className="w-2/12 pl-6">Is Admin</div>
          <div className="w-2/12 pl-6">Active</div>

          <div className="w-2/12 pl-6"></div>
        </div>
      </div>
      {userListData?.map((user_obj, index) => {
        return (
          <UserListRow
            {...user_obj}
            key={`user-${index}`}
            onEdit={() => {
              setSelectedUser(user_obj)
              setShowModal(true)
            }}
            onDelete={() => {
              deleteUser(user_obj)
            }}
            showDeleted={showDeleted}
          />
        )
      })}
      {userListData?.length < 1 && (
        <NoRecordsFound caption={"User's Not Found"} loading={false} />
      )}
      <div className="py-6">
        <Pagination
          context={pagination}
          onClickNext={(e) => {
            getUserList(e, !showDeleted)
          }}
          onClickPrev={(e) => {
            getUserList(e, !showDeleted)
          }}
        />
      </div>
    </>
  )
}

export default UserList
